import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Play, Upload, Loader2, CheckCircle2, Clock, MessageSquare, X, FolderOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { challengesAPI, uploadAPI } from '../lib/api';
import HLSPlayer from './HLSPlayer';
import VideoSourcePicker from './VideoSourcePicker';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Weekly Challenge box for the player UI.
 * Shows the coach's video, the challenge prompt, and a button to upload the player's submission.
 * Hidden if no challenge exists for this week.
 */
export default function WeeklyChallengeCard({ compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [notes, setNotes] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await challengesAPI.current();
      setData(res.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleUpload = async (file) => {
    if (!file || !data?.challenge) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const upRes = await uploadAPI.video(file, (pe) => {
        setUploadProgress(Math.round((pe.loaded * 100) / pe.total));
      });
      await challengesAPI.submit({
        challenge_id: data.challenge.id,
        video_filename: upRes.data.filename,
        notes,
      });
      toast.success('Challenge submitted! Coach will review.');
      setShowUpload(false);
      setNotes('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    setUploadProgress(0);
  };

  if (loading || !data?.challenge) return null;

  const { challenge, submission } = data;
  const submitted = !!submission;
  const reviewed = submission?.status === 'reviewed';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br from-[#FF9100]/15 to-[#FFB347]/5 border border-[#FF9100]/30 rounded-2xl overflow-hidden ${compact ? '' : 'shadow-lg'}`}
      data-testid="weekly-challenge-card"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#FF9100]/10 border-b border-[#FF9100]/20">
        <div className="flex items-center gap-2">
          <Trophy size={14} className="text-[#FF9100]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9100]">
            Week {challenge.week_number} Challenge
          </span>
        </div>
        {submitted && (
          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            reviewed ? 'bg-[#10B981] text-white' : 'bg-[#2563EB] text-white'
          }`}>
            {reviewed ? 'Reviewed' : 'Submitted'}
          </span>
        )}
      </div>

      {/* Video */}
      {challenge.video_filename && (
        <HLSPlayer
          videoFilename={challenge.video_filename}
          watermarkText={user?.name || user?.email}
          className="w-full aspect-video bg-black"
          data-testid="challenge-video"
        />
      )}

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight text-[#0A2242]">
          {challenge.title}
        </h3>
        {challenge.description && (
          <p className="text-xs text-[#64748B] leading-relaxed">{challenge.description}</p>
        )}
        {challenge.prompt && (
          <div className="bg-white border border-[#FF9100]/20 rounded-lg p-3">
            <p className="text-[9px] uppercase tracking-widest text-[#FF9100] font-bold mb-1">Your Mission</p>
            <p className="text-xs text-[#0A2242]">{challenge.prompt}</p>
          </div>
        )}

        {/* Submission state */}
        {submitted && reviewed && submission.coach_response && (
          <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageSquare size={11} className="text-[#10B981]" />
              <p className="text-[9px] uppercase tracking-widest text-[#10B981] font-bold">Coach Feedback</p>
            </div>
            <p className="text-xs text-[#0A2242]">{submission.coach_response}</p>
          </div>
        )}

        {submitted && !reviewed && (
          <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-lg p-3 flex items-center gap-2">
            <Clock size={12} className="text-[#2563EB]" />
            <p className="text-xs text-[#2563EB] font-medium">Submitted! Awaiting coach review.</p>
          </div>
        )}

        {/* CTA */}
        {!showUpload ? (
          <button
            onClick={() => setShowUpload(true)}
            className="w-full bg-[#FF9100] hover:bg-[#FFA733] text-white font-bold uppercase tracking-wide rounded-lg px-4 py-3 text-xs transition-colors flex items-center justify-center gap-2"
            data-testid="challenge-upload-btn"
          >
            <Upload size={14} />
            {submitted ? 'Resubmit Challenge Video' : 'Upload Your Challenge Video'}
          </button>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">New Submission</p>
              <button onClick={() => { setShowUpload(false); setNotes(''); }} className="text-[#64748B]"><X size={14} /></button>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional note for your coach..."
              className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-2 py-2 text-xs text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#FF9100] resize-none h-16"
              data-testid="challenge-note-input"
            />
            {uploading ? (
              <div className="w-full border-2 border-dashed border-[#FF9100]/60 rounded-md p-3 text-center text-xs flex items-center justify-center gap-2" data-testid="challenge-uploading">
                <Loader2 size={14} className="animate-spin text-[#FF9100]" /> Uploading {uploadProgress}%
              </div>
            ) : (
              <VideoSourcePicker onFileSelected={handleUpload} disabled={uploading} compact />
            )}
            {uploading && (
              <div className="w-full bg-[#E2E8F0] rounded-full h-1">
                <div className="bg-[#FF9100] h-1 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
          </div>
        )}

        {/* View Past Submissions link */}
        <button
          onClick={() => navigate('/my-challenges')}
          className="w-full flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#64748B] hover:text-[#FF9100] transition-colors pt-1"
          data-testid="view-my-challenges-link"
        >
          <FolderOpen size={11} /> View All My Challenge Videos
        </button>
      </div>
    </motion.div>
  );
}
